/**
 * RO Efficiency tab: the arithmetic of the client's own Roll Off Efficiency
 * Tracker, on canonical service_event rows.
 *
 * WHAT THIS REPRODUCES. The tracker a hauler's ops lead built and runs every
 * day: completed roll off jobs by driver by day, division hauls and
 * deliveries by day, a month stats panel (hauls, deliveries, working days,
 * hauls per working day, average drivers, hauls per driver), driver totals
 * across months, front load deliveries and removals by driver, and a
 * scheduled vs completed board for one date. Every rule below is the
 * tracker's rule, read from its code, not one of ours:
 *
 *   - A job is dated by its SCHEDULED date (service_event.event_date).
 *   - Cancelled jobs and jobs on an excluded route (the tracker's DELETE
 *     route flag) do not exist for any count, scheduled or completed.
 *   - A completed job is a HAUL when its work type is in the customer's
 *     haul list, a DELIVERY when in the delivery list, otherwise OTHER.
 *     Only completed jobs are hauls, deliveries or other.
 *   - A WORKING DAY is a weekday whose company-wide haul count reaches the
 *     customer's threshold. The threshold is the customer's own number
 *     (config); with none configured every per-day figure is withheld,
 *     never computed against a default.
 *   - Per-day figures are SUM over SUM across the working days (MER-715):
 *     hauls on working days / working days, driver-days on working days /
 *     working days, and hauls per driver = hauls on working days /
 *     driver-days on working days. No per-day rate is ever averaged.
 *
 * The work type lists, the threshold and the named route rows are the
 * customer's (customers/<slug>/config.yaml ro_efficiency, injected into the
 * widget by resolve-manifest.mjs). Nothing in this file names a customer.
 *
 * Pure and dependency-free so node:test drives it directly with synthetic
 * rows (scripts/ro-efficiency.test.mjs); roEfficiency.d.mts types it.
 */

/** Driver ids that name nobody. A job with one of these has no driver. */
export const NO_DRIVER = Object.freeze(["", "unknown", "nan", "none", "null"]);

/** The tracker's own label for a completed job that is neither haul nor delivery. */
export const CATEGORY = Object.freeze({
  HAUL: "haul",
  DELIVERY: "delivery",
  OTHER: "other",
});

function str(v) {
  return v === null || v === undefined ? "" : String(v).trim();
}

/** 'YYYY-MM-DD' from a date or timestamp value, or null. */
export function dayKey(value) {
  const s = str(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** Every 'YYYY-MM-DD' in a 'YYYY-MM' month, in order. */
export function daysInMonth(month) {
  const m = str(month).match(/^(\d{4})-(\d{2})$/);
  if (!m) return [];
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const last = new Date(y, mo, 0).getDate();
  const out = [];
  for (let d = 1; d <= last; d += 1) out.push(`${m[1]}-${m[2]}-${String(d).padStart(2, "0")}`);
  return out;
}

/** True for Saturday and Sunday. Local calendar parts, never UTC. */
export function isWeekend(day) {
  const m = str(day).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const dow = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay();
  return dow === 0 || dow === 6;
}

/**
 * A route id without the division suffix the pipeline appends to some
 * routes ("RO 2 (Jackson)" -> "RO 2"), so a route named in the customer's
 * config matches whichever spelling the row carries.
 */
export function routeBase(routeId) {
  return str(routeId).replace(/\s*\([^()]*\)\s*$/, "");
}

/** The driver a row names, or null when it names nobody. */
export function driverOf(row) {
  const d = str(row?.driver_id);
  return NO_DRIVER.includes(d.toLowerCase()) ? null : d;
}

function lower(v) {
  return str(v).toLowerCase();
}

/**
 * Is this row out of every count? Cancelled, or on a route whose base name
 * starts with one of the customer's excluded route prefixes (the tracker's
 * DELETE flag; the pipeline spells it "DELETE_roll_off (Jackson)").
 */
export function isExcluded(row, cfg) {
  if (lower(row?.completion_status) === "cancelled") return true;
  const prefixes = Array.isArray(cfg?.excluded_route_prefixes) ? cfg.excluded_route_prefixes : [];
  const route = routeBase(row?.route_id).toLowerCase();
  return prefixes.some((p) => typeof p === "string" && p && route.startsWith(p.toLowerCase()));
}

export function isCompleted(row) {
  return lower(row?.completion_status) === "completed";
}

/**
 * Haul / delivery / other for a job of one line, regardless of completion
 * (the tracker's roWorkTypeBucket, used by the scheduled board).
 */
export function workTypeBucket(row, lineCfg) {
  const wt = str(row?.work_type);
  if ((lineCfg?.haul_work_types ?? []).includes(wt)) return CATEGORY.HAUL;
  if ((lineCfg?.delivery_work_types ?? []).includes(wt)) return CATEGORY.DELIVERY;
  return CATEGORY.OTHER;
}

/** The tracker's categoryFor: a bucket for a completed job, null otherwise. */
export function categoryOf(row, lineCfg) {
  return isCompleted(row) ? workTypeBucket(row, lineCfg) : null;
}

/**
 * The rows of one line that exist for counting: this line's equipment type,
 * not excluded. `line` is a canonical equipment_type (roll_off, hook_lift,
 * tractor_trailer).
 */
export function lineRows(rows, line, cfg) {
  return (rows ?? []).filter(
    (r) => str(r?.equipment_type) === line && !isExcluded(r, cfg) && dayKey(r?.event_date),
  );
}

/**
 * The lines the RO Efficiency page can switch between, in display order, with
 * the short name its labels print. Hook lift and tractor trailer are their own
 * lines with their own counts, never pooled with roll off (AJ 2026-10-21,
 * #1057).
 */
export const RO_LINE_TABS = Object.freeze([
  Object.freeze({ line: "roll_off", label: "Roll Off", short: "RO" }),
  Object.freeze({ line: "hook_lift", label: "Hook Lift", short: "HL" }),
  Object.freeze({ line: "tractor_trailer", label: "Tractor Trailer", short: "TT" }),
]);

/**
 * The lines this tenant's resolved RO Efficiency widgets offer: a line is
 * offered when a panel for it (not the front load panel) carries haul work
 * types from the customer's config. Each entry carries that panel's resolved
 * block, so the page counts the line on its own work types. A tenant whose
 * config names one line gets one entry and the page draws no switcher.
 *
 * @param {Array<{ro_efficiency?: object}>} widgets
 */
export function offeredRoLines(widgets) {
  const cfgs = (widgets ?? []).map((w) => w?.ro_efficiency).filter(Boolean);
  const out = [];
  for (const tab of RO_LINE_TABS) {
    const config = cfgs.find(
      (c) =>
        c.panel !== "fel_drivers" &&
        c.line === tab.line &&
        Array.isArray(c.line_config?.haul_work_types) &&
        c.line_config.haul_work_types.length > 0,
    );
    if (config) out.push({ ...tab, config });
  }
  return out;
}

function inMonth(row, month) {
  const d = dayKey(row?.event_date);
  return d !== null && d.startsWith(`${month}-`);
}

function divisionMatch(row, divisions) {
  if (!Array.isArray(divisions) || divisions.length === 0) return true;
  const d = lower(row?.division);
  return divisions.some((x) => lower(x) === d);
}

/** Per-day completed counts by category for a set of line rows. */
function dayCounts(rows, lineCfg) {
  const byDay = new Map();
  for (const r of rows) {
    const cat = categoryOf(r, lineCfg);
    if (!cat) continue;
    const day = dayKey(r.event_date);
    let c = byDay.get(day);
    if (!c) {
      c = { haul: 0, delivery: 0, other: 0, drivers: new Set() };
      byDay.set(day, c);
    }
    c[cat] += 1;
    const drv = driverOf(r);
    if (drv) c.drivers.add(drv);
  }
  return byDay;
}

/**
 * The working days of a month: weekdays whose COMPANY-WIDE completed hauls
 * on this line reach the threshold. Company-wide on purpose: the tracker
 * decides a holiday from the whole operation, so narrowing the page to one
 * division must not turn every day of a small division into a holiday.
 *
 * @returns {string[] | null} null when no threshold is configured.
 */
export function workingDays(allLineRows, month, lineCfg) {
  const min = lineCfg?.working_day_min_hauls;
  if (typeof min !== "number" || !Number.isFinite(min) || min <= 0) return null;
  const counts = dayCounts(
    allLineRows.filter((r) => inMonth(r, month)),
    lineCfg,
  );
  return daysInMonth(month).filter((d) => !isWeekend(d) && (counts.get(d)?.haul ?? 0) >= min);
}

function ratio(num, den) {
  return den > 0 ? num / den : null;
}

/**
 * The month stats panel (the tracker's RO Stats).
 *
 * hauls / deliveries / other: every completed job in the month, every day.
 * The per-day figures run over the working days only and are null when
 * there is no threshold or no working day.
 */
export function monthStats(allLineRows, month, lineCfg, divisions) {
  const monthRows = allLineRows.filter((r) => inMonth(r, month));
  const scoped = monthRows.filter((r) => divisionMatch(r, divisions));
  let hauls = 0;
  let deliveries = 0;
  let other = 0;
  for (const r of scoped) {
    const cat = categoryOf(r, lineCfg);
    if (cat === CATEGORY.HAUL) hauls += 1;
    else if (cat === CATEGORY.DELIVERY) deliveries += 1;
    else if (cat === CATEGORY.OTHER) other += 1;
  }
  const wd = workingDays(allLineRows, month, lineCfg);
  const out = {
    rows: monthRows.length,
    hauls,
    deliveries,
    other,
    thresholdConfigured: wd !== null,
    workingDays: wd === null ? null : wd.length,
    workingDayHauls: null,
    workingDayDeliveries: null,
    driverDays: null,
    haulsPerDay: null,
    deliveriesPerDay: null,
    avgDrivers: null,
    haulsPerDriver: null,
  };
  if (wd === null) return out;
  const counts = dayCounts(scoped, lineCfg);
  let wh = 0;
  let wdl = 0;
  let dd = 0;
  for (const d of wd) {
    const c = counts.get(d);
    if (!c) continue;
    wh += c.haul;
    wdl += c.delivery;
    dd += c.drivers.size;
  }
  out.workingDayHauls = wh;
  out.workingDayDeliveries = wdl;
  out.driverDays = dd;
  out.haulsPerDay = ratio(wh, wd.length);
  out.deliveriesPerDay = ratio(wdl, wd.length);
  out.avgDrivers = ratio(dd, wd.length);
  // haulsPerDay / avgDrivers with the day count cancelled: SUM over SUM.
  out.haulsPerDriver = ratio(wh, dd);
  return out;
}

function modeDivision(counts) {
  let best = null;
  let bestN = -1;
  for (const [div, n] of counts) {
    if (n > bestN || (n === bestN && String(div) < String(best))) {
      best = div;
      bestN = n;
    }
  }
  return best;
}

/**
 * The driver by day grid: completed jobs (haul + delivery + other) per
 * driver per day, grouped under the division each driver worked most that
 * month, with a Total row per division, then the customer's named route
 * rows (hook truck, broker and the like), matched on the route's base name.
 *
 * Goals are not here: they are entered by management and have no source
 * yet, so the grid shows production only.
 */
export function driverDailyGrid(allLineRows, month, lineCfg, divisions, routeRows) {
  const days = daysInMonth(month);
  const scoped = allLineRows.filter(
    (r) => inMonth(r, month) && divisionMatch(r, divisions) && categoryOf(r, lineCfg),
  );
  const byDriver = new Map();
  for (const r of scoped) {
    const drv = driverOf(r) ?? "Unassigned";
    let e = byDriver.get(drv);
    if (!e) {
      e = { label: drv, counts: {}, total: 0, divs: new Map(), jobs: {} };
      byDriver.set(drv, e);
    }
    const day = dayKey(r.event_date);
    e.counts[day] = (e.counts[day] ?? 0) + 1;
    e.total += 1;
    const div = str(r.division) || "Unknown";
    e.divs.set(div, (e.divs.get(div) ?? 0) + 1);
    (e.jobs[day] ??= []).push({
      event_id: str(r.event_id),
      route: str(r.route_id),
      work_type: str(r.work_type),
      category: categoryOf(r, lineCfg),
    });
  }
  const groups = new Map();
  for (const e of byDriver.values()) {
    const div = e.label === "Unassigned" ? "Unassigned" : modeDivision(e.divs);
    if (!groups.has(div)) groups.set(div, []);
    groups.get(div).push({ label: e.label, counts: e.counts, total: e.total, jobs: e.jobs });
  }
  const divisionGroups = [...groups.entries()]
    .sort(([a], [b]) => (a === "Unassigned") - (b === "Unassigned") || a.localeCompare(b))
    .map(([division, drivers]) => {
      drivers.sort((a, b) => a.label.localeCompare(b.label));
      const totals = {};
      let total = 0;
      for (const d of drivers) {
        for (const [day, n] of Object.entries(d.counts)) totals[day] = (totals[day] ?? 0) + n;
        total += d.total;
      }
      return { division, drivers, totals, total };
    });
  const routes = (Array.isArray(routeRows) ? routeRows : [])
    .filter((rr) => rr && typeof rr.label === "string" && Array.isArray(rr.routes))
    .map((rr) => {
      const names = new Set(rr.routes.map((x) => str(x)));
      const counts = {};
      let total = 0;
      for (const r of scoped) {
        if (!names.has(routeBase(r.route_id))) continue;
        const day = dayKey(r.event_date);
        counts[day] = (counts[day] ?? 0) + 1;
        total += 1;
      }
      return { label: rr.label, routes: [...names], counts, total };
    });
  return { days, divisionGroups, routes };
}

/**
 * Division rows by day: per division, completed jobs, hauls and deliveries;
 * then company Total Hauls and Total Deliveries. Divisions come from the
 * manifest order first, then any other division the rows carry.
 */
export function divisionDailyGrid(allLineRows, month, lineCfg, divisionOrder, divisions) {
  const days = daysInMonth(month);
  const scoped = allLineRows.filter((r) => inMonth(r, month) && divisionMatch(r, divisions));
  const byDiv = new Map();
  const seen = [];
  for (const r of scoped) {
    const cat = categoryOf(r, lineCfg);
    if (!cat) continue;
    const label = str(r.division) || "Unknown";
    const key = label.toLowerCase();
    let e = byDiv.get(key);
    if (!e) {
      e = { division: label, completed: {}, hauls: {}, deliveries: {}, totals: {} };
      byDiv.set(key, e);
      seen.push(key);
    }
    const day = dayKey(r.event_date);
    e.completed[day] = (e.completed[day] ?? 0) + 1;
    if (cat === CATEGORY.HAUL) e.hauls[day] = (e.hauls[day] ?? 0) + 1;
    if (cat === CATEGORY.DELIVERY) e.deliveries[day] = (e.deliveries[day] ?? 0) + 1;
  }
  const order = (Array.isArray(divisionOrder) ? divisionOrder : []).map((d) => lower(d));
  const keys = [
    ...order.filter((k) => byDiv.has(k)),
    ...seen.filter((k) => !order.includes(k)).sort(),
  ];
  const sum = (obj) => Object.values(obj).reduce((a, b) => a + b, 0);
  const rows = keys.map((k) => {
    const e = byDiv.get(k);
    return {
      ...e,
      totals: {
        completed: sum(e.completed),
        hauls: sum(e.hauls),
        deliveries: sum(e.deliveries),
      },
    };
  });
  const totalHauls = {};
  const totalDeliveries = {};
  for (const e of rows) {
    for (const [d, n] of Object.entries(e.hauls)) totalHauls[d] = (totalHauls[d] ?? 0) + n;
    for (const [d, n] of Object.entries(e.deliveries))
      totalDeliveries[d] = (totalDeliveries[d] ?? 0) + n;
  }
  return {
    days,
    rows,
    totalHauls,
    totalDeliveries,
    grand: { hauls: sum(totalHauls), deliveries: sum(totalDeliveries) },
  };
}

/** 'YYYY-MM' months from January of `month`'s year through `month`. */
export function monthsThrough(month) {
  const m = str(month).match(/^(\d{4})-(\d{2})$/);
  if (!m) return [];
  const out = [];
  for (let i = 1; i <= Number(m[2]); i += 1) out.push(`${m[1]}-${String(i).padStart(2, "0")}`);
  return out;
}

/**
 * Driver totals (the tracker's Driver Totals tab).
 *
 * byMonth: hauls per driver per month from January through `month`, with
 * Avg/Day = the driver's hauls over the days that driver hauled (SUM over
 * SUM, the tracker's own denominator; it is a per-driver pace, not a
 * company rate). single: for `month`, hauls, deliveries, total completed
 * and average hauls per day hauled.
 */
export function driverTotals(allLineRows, month, lineCfg, divisions) {
  const months = monthsThrough(month);
  const set = new Set(months);
  const scoped = allLineRows.filter(
    (r) =>
      set.has(String(dayKey(r.event_date)).slice(0, 7)) &&
      divisionMatch(r, divisions) &&
      categoryOf(r, lineCfg),
  );
  const drivers = new Map();
  for (const r of scoped) {
    const drv = driverOf(r);
    if (!drv) continue;
    let e = drivers.get(drv);
    if (!e) {
      e = {
        label: drv,
        hauls: {},
        haulDays: new Set(),
        single: { hauls: 0, deliveries: 0, total: 0, haulDays: new Set() },
      };
      drivers.set(drv, e);
    }
    const day = dayKey(r.event_date);
    const ym = day.slice(0, 7);
    const cat = categoryOf(r, lineCfg);
    if (cat === CATEGORY.HAUL) {
      e.hauls[ym] = (e.hauls[ym] ?? 0) + 1;
      e.haulDays.add(day);
    }
    if (ym === month) {
      e.single.total += 1;
      if (cat === CATEGORY.HAUL) {
        e.single.hauls += 1;
        e.single.haulDays.add(day);
      }
      if (cat === CATEGORY.DELIVERY) e.single.deliveries += 1;
    }
  }
  const list = [...drivers.values()].sort((a, b) => a.label.localeCompare(b.label));
  const byMonth = list
    .map((e) => {
      const total = Object.values(e.hauls).reduce((a, b) => a + b, 0);
      return { label: e.label, hauls: e.hauls, total, avgPerDay: ratio(total, e.haulDays.size) };
    })
    .filter((e) => e.total > 0);
  const single = list
    .filter((e) => e.single.total > 0)
    .map((e) => ({
      label: e.label,
      hauls: e.single.hauls,
      deliveries: e.single.deliveries,
      total: e.single.total,
      avgPerDay: ratio(e.single.hauls, e.single.haulDays.size),
    }));
  return { months, byMonth, single };
}

/**
 * Front load deliveries and removals by driver by day (the tracker's FEL
 * Drivers tab): completed jobs on the customer's front load delivery and
 * removal work types, no goals.
 */
export function felDriverGrid(rows, month, felCfg, cfg, divisions) {
  const del = new Set((felCfg?.delivery_work_types ?? []).map(str));
  const rem = new Set((felCfg?.removal_work_types ?? []).map(str));
  const days = daysInMonth(month);
  const drivers = new Map();
  for (const r of rows ?? []) {
    if (str(r?.equipment_type) !== "front_load") continue;
    if (isExcluded(r, cfg) || !isCompleted(r) || !inMonth(r, month)) continue;
    if (!divisionMatch(r, divisions)) continue;
    const wt = str(r.work_type);
    const kind = del.has(wt) ? "deliveries" : rem.has(wt) ? "removals" : null;
    if (!kind) continue;
    const drv = driverOf(r) ?? "Unassigned";
    let e = drivers.get(drv);
    if (!e) {
      e = { label: drv, deliveries: {}, removals: {}, totals: { deliveries: 0, removals: 0 } };
      drivers.set(drv, e);
    }
    const day = dayKey(r.event_date);
    e[kind][day] = (e[kind][day] ?? 0) + 1;
    e.totals[kind] += 1;
  }
  const list = [...drivers.values()].sort((a, b) => a.label.localeCompare(b.label));
  return { days, drivers: list };
}

/**
 * Scheduled vs completed for one date, by division (the tracker's RO
 * Scheduled tab). Scheduled is every job on the books that day that is not
 * excluded, any status; completed is the completed subset. Drivers is the
 * number of distinct drivers with a completed job that day; driversScheduled
 * the distinct drivers named on any of the day's jobs (the tracker's
 * "# Drivers" Scheduled column).
 */
export function scheduledVsCompleted(allLineRows, day, lineCfg, divisionOrder, divisions) {
  const target = dayKey(day);
  const scoped = allLineRows.filter(
    (r) => dayKey(r.event_date) === target && divisionMatch(r, divisions),
  );
  const byDiv = new Map();
  const blank = (division) => ({
    division,
    scheduled: 0,
    completed: 0,
    drivers: new Set(),
    driversScheduled: new Set(),
    haulsScheduled: 0,
    haulsCompleted: 0,
    deliveriesScheduled: 0,
    deliveriesCompleted: 0,
  });
  for (const r of scoped) {
    const label = str(r.division) || "Unknown";
    const key = label.toLowerCase();
    if (!byDiv.has(key)) byDiv.set(key, blank(label));
    const e = byDiv.get(key);
    const bucket = workTypeBucket(r, lineCfg);
    const done = isCompleted(r);
    e.scheduled += 1;
    const named = driverOf(r);
    if (named) e.driversScheduled.add(named);
    if (bucket === CATEGORY.HAUL) e.haulsScheduled += 1;
    if (bucket === CATEGORY.DELIVERY) e.deliveriesScheduled += 1;
    if (done) {
      e.completed += 1;
      if (bucket === CATEGORY.HAUL) e.haulsCompleted += 1;
      if (bucket === CATEGORY.DELIVERY) e.deliveriesCompleted += 1;
      const drv = driverOf(r);
      if (drv) e.drivers.add(drv);
    }
  }
  const order = (Array.isArray(divisionOrder) ? divisionOrder : []).map((d) => lower(d));
  const keys = [
    ...order.filter((k) => byDiv.has(k)),
    ...[...byDiv.keys()].filter((k) => !order.includes(k)).sort(),
  ];
  const rows = keys.map((k) => {
    const e = byDiv.get(k);
    return { ...e, drivers: e.drivers.size, driversScheduled: e.driversScheduled.size };
  });
  const total = blank("Total");
  const allDrivers = new Set();
  const allScheduledDrivers = new Set();
  for (const k of keys) {
    const e = byDiv.get(k);
    for (const f of [
      "scheduled",
      "completed",
      "haulsScheduled",
      "haulsCompleted",
      "deliveriesScheduled",
      "deliveriesCompleted",
    ])
      total[f] += e[f];
    for (const d of e.drivers) allDrivers.add(d);
    for (const d of e.driversScheduled) allScheduledDrivers.add(d);
  }
  return {
    day: target,
    rows,
    total: { ...total, drivers: allDrivers.size, driversScheduled: allScheduledDrivers.size },
  };
}

/** The latest scheduled date in the rows that is on or before `today` ('YYYY-MM-DD'). */
export function latestDayOnOrBefore(rows, today) {
  let best = null;
  for (const r of rows ?? []) {
    const d = dayKey(r?.event_date);
    if (d && d <= today && (best === null || d > best)) best = d;
  }
  return best;
}

/**
 * The newest date on or before `today` ('YYYY-MM-DD') that has at least one
 * completed job. Rows still scheduled for a later day never move it, which
 * is what the "Data through" chip means (the client's SEED_THROUGH).
 */
export function latestCompletedDayOnOrBefore(rows, today) {
  return latestDayOnOrBefore(
    (rows ?? []).filter((r) => isCompleted(r)),
    today,
  );
}

/**
 * Display name for a division on the RO Efficiency panels. The customer
 * config's division_labels (stamped on the panel by resolve-manifest) wins
 * ("central_coast" -> "Crystal Coast"); otherwise the id is title-cased
 * ("main" -> "Main") and a value that already carries capitals ("Jackson")
 * is left alone. The id itself stays the key for filtering and React keys.
 *
 * @param {unknown} division
 * @param {Record<string, string> | null | undefined} labels
 * @returns {string}
 */
export function roDivisionLabel(division, labels) {
  const s = String(division ?? "").trim();
  if (!s) return "Unassigned";
  const key = s.toLowerCase().replace(/\s+/g, "_");
  const mapped = labels && typeof labels === "object" ? labels[key] : undefined;
  if (typeof mapped === "string" && mapped.trim()) return mapped.trim();
  if (/[A-Z]/.test(s)) return s;
  return s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * The tracker's cleanDriverName: a dispatch driver name carries a trailing
 * route-code token ("Alex Davis 6", "Carlos Frazier F3", "James Walker HT2");
 * strip it to the display name. Matches on the pattern, not a lookup table,
 * as the tracker does. null for an empty name.
 *
 * @param {unknown} name
 * @returns {string | null}
 */
export function cleanDriverName(name) {
  const s = str(name);
  if (!s) return null;
  return s.replace(/\s+[A-Z]{0,3}\d+$/, "").trim() || null;
}

/**
 * The tracker's RO Stats block (renderStatsPanel), from monthStats.
 *
 * The tracker divides the FULL month's hauls and deliveries (every day,
 * working or not) by the working-day count: the working-day rule thins the
 * denominator only. Avg Drivers is the driver-days on working days over the
 * working days, and hauls per driver is hauls per day over avg drivers. With
 * no working day every figure is 0, as the tracker prints it. This differs
 * from monthStats' haulsPerDay (working-day hauls over working days,
 * MER-715); the tracker port uses this one (AJ 2026-09-28: the page wins).
 *
 * @param {{hauls: number, deliveries: number, workingDays: number | null, driverDays: number | null}} stats
 */
export function trackerStats(stats) {
  const hauls = stats?.hauls ?? 0;
  const deliveries = stats?.deliveries ?? 0;
  const days = stats?.workingDays;
  if (typeof days !== "number") {
    return {
      hauls,
      deliveries,
      workingDays: null,
      haulsPerDay: null,
      deliveriesPerDay: null,
      avgDrivers: null,
      haulsPerDriver: null,
    };
  }
  const haulsPerDay = days ? hauls / days : 0;
  const deliveriesPerDay = days ? deliveries / days : 0;
  const avgDrivers = days ? (stats?.driverDays ?? 0) / days : 0;
  const haulsPerDriver = avgDrivers ? haulsPerDay / avgDrivers : 0;
  return {
    hauls,
    deliveries,
    workingDays: days,
    haulsPerDay,
    deliveriesPerDay,
    avgDrivers,
    haulsPerDriver,
  };
}

/**
 * The tracker's month roster with the drivers it does not list added in.
 *
 * `layout` is the customer's roster for the month, the tracker's own
 * MONTH_LAYOUT shape: [label, 'driver' | 'route', routes, goal] rows and
 * [label, sectionKey] total rows. `sections` names the division each total
 * row closes, in roster order ({"total-jax": "Jackson", ...}). A driver with
 * completed jobs in the month who is not on the roster gets a row with no
 * goal, placed before the total of the first section's division when he
 * worked it at least as often as the second's, else before the second's
 * (the tracker's Jackson >= Hattiesburg rule). With no section rows the
 * extras go at the end. Extras keep the order the rows first name them.
 *
 * @param {Array<Array<unknown>>} layout
 * @param {Array<object>} allLineRows rows whose driver_id is already the roster label
 * @param {string} month 'YYYY-MM'
 * @param {object} lineCfg
 * @param {Record<string, string>} sections
 */
export function trackerRoster(layout, allLineRows, month, lineCfg, sections) {
  const roster = Array.isArray(layout) ? layout : [];
  const known = new Set(roster.filter((e) => e[1] === "driver").map((e) => e[0]));
  const sectionKeys = roster.map((e) => e[1]).filter((k) => sections && k in sections);
  const extra = new Map();
  for (const r of allLineRows ?? []) {
    if (!inMonth(r, month) || !categoryOf(r, lineCfg)) continue;
    const drv = driverOf(r);
    if (!drv || known.has(drv)) continue;
    if (!extra.has(drv)) extra.set(drv, new Map());
    const c = extra.get(drv);
    const div = lower(r.division);
    c.set(div, (c.get(div) ?? 0) + 1);
  }
  const bySection = new Map(sectionKeys.map((k) => [k, []]));
  const tail = [];
  for (const [label, c] of extra) {
    const entry = [label, "driver", null, null];
    if (sectionKeys.length === 0) tail.push(entry);
    else if (sectionKeys.length === 1) bySection.get(sectionKeys[0]).push(entry);
    else {
      const a = c.get(lower(sections[sectionKeys[0]])) ?? 0;
      const b = c.get(lower(sections[sectionKeys[1]])) ?? 0;
      bySection.get(a >= b ? sectionKeys[0] : sectionKeys[1]).push(entry);
    }
  }
  const out = [];
  for (const e of roster) {
    if (bySection.has(e[1])) out.push(...bySection.get(e[1]));
    out.push(e);
  }
  return [...out, ...tail];
}

/**
 * Every column the arithmetic above reads from a work order. event_id and
 * site_name are not here: only the cell detail lists them, and it reads its
 * own day of rows when a cell is clicked. A grouped read (one row per
 * distinct combination of these columns plus a count) carries everything
 * every count on the page needs.
 */
export const RO_GROUP_COLUMNS = Object.freeze([
  "event_date",
  "division",
  "driver_id",
  "route_id",
  "work_type",
  "completion_status",
  "equipment_type",
]);

/**
 * Grouped rows back to work order rows: a group of `count` jobs becomes
 * `count` rows carrying the group's columns. Every function above reads only
 * RO_GROUP_COLUMNS, so on the expanded rows it gives exactly what it gives
 * on the work orders themselves (pinned in scripts/ro-efficiency.test.mjs).
 * A group without a positive whole count adds no row.
 *
 * Order: groups are put in (scheduled date, first_event_id) order, where
 * first_event_id is the smallest event_id in the group, which is the order
 * a row read by (event_date, event_id) returns (ties keep input order). Each thing the functions
 * above list in first-seen order (the roster's extra drivers) first appears
 * in its earliest group, so it is listed in the order the row read gave.
 * Within one day, jobs of different groups can come out in another order,
 * so driverDailyGrid's per-job `jobs` lists hold the same jobs in a possibly
 * different order; the report does not draw them (its cell detail reads its
 * own rows).
 * Ids compare as strings, as the database orders the digit-only text ids.
 *
 * @param {Array<Record<string, unknown>>} groups
 */
export function expandGroupedRows(groups) {
  const list = (groups ?? []).filter((g) => g && typeof g === "object");
  const key = (g) => [dayKey(g.event_date) ?? "", str(g.first_event_id)];
  const ordered = list
    .map((g, i) => ({ g, i, k: key(g) }))
    .sort(
      (a, b) =>
        (a.k[0] < b.k[0] ? -1 : a.k[0] > b.k[0] ? 1 : 0) ||
        (a.k[1] < b.k[1] ? -1 : a.k[1] > b.k[1] ? 1 : 0) ||
        a.i - b.i,
    );
  const out = [];
  for (const { g } of ordered) {
    const n = Number(g.count);
    if (!Number.isInteger(n) || n <= 0) continue;
    const row = {};
    for (const c of RO_GROUP_COLUMNS) row[c] = g[c] ?? null;
    for (let i = 0; i < n; i += 1) out.push({ ...row });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Goal overrides (0090 ro_goal_override)                              */
/* ------------------------------------------------------------------ */

/** "YYYY-MM" for a month stored as a date ("2026-09-01") or already a month. */
function monthOf(m) {
  return String(m ?? "").slice(0, 7);
}

/**
 * ro_goal_override rows to a lookup keyed "YYYY-MM|label". A null goal is a
 * cleared override and is left out, so the roster default applies.
 */
export function goalOverrideMap(rows) {
  const out = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || r.goal === null || r.goal === undefined || r.goal === "") continue;
    const v = Number(r.goal);
    if (!Number.isFinite(v)) continue;
    out.set(`${monthOf(r.month)}|${r.label}`, v);
  }
  return out;
}

/** The page's effectiveGoal(): the month's override for the label, else the roster default. */
export function effectiveGoal(overrides, month, label, defaultGoal) {
  const v = overrides?.get(`${monthOf(month)}|${label}`);
  return v !== undefined ? v : (defaultGoal ?? null);
}

/* ------------------------------------------------------------------ */
/* Driver list settings (0108 ro_driver_setting)                       */
/* ------------------------------------------------------------------ */

/**
 * ro_driver_setting rows to a lookup by driver label:
 * { hiddenFrom: "YYYY-MM" | null, division: string | null }. A row with
 * neither set is a cleared setting and is left out.
 */
export function driverSettingMap(rows) {
  const out = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || typeof r.label !== "string" || !r.label.trim()) continue;
    const hiddenFrom = /^\d{4}-\d{2}/.test(String(r.hidden_from ?? ""))
      ? monthOf(r.hidden_from)
      : null;
    const division = str(r.division) || null;
    if (!hiddenFrom && !division) continue;
    out.set(r.label, { hiddenFrom, division });
  }
  return out;
}

/** Is the driver off the month's list: hidden from this month or an earlier one? */
export function isDriverHidden(settings, label, month) {
  const from = settings?.get(label)?.hiddenFrom;
  return Boolean(from) && monthOf(month) >= from;
}

/**
 * The month's list with the tenant's driver settings applied, on the
 * tracker's roster (trackerRoster's output, so drivers off the roster who
 * worked the month are in it).
 *
 * - A driver listed under another division (setting.division) moves to the
 *   end of that division's section, just before its Total row. A division
 *   with no Total row of its own (Delta's Dallas, after the last Total) is
 *   the end of the list. A driver already in that section stays where he is.
 * - A driver hidden from this month or earlier stays in his place with type
 *   'hidden-driver': the grid draws no row for him, and still adds his
 *   completed jobs to the section Total. The client's page never leaves a
 *   driver's completed jobs out of a total (its roster check adds any
 *   driver who worked the month), so hiding takes the row away and nothing
 *   else: the division rows, Total Hauls / Total Deliveries and the stats
 *   count work orders by division and never read the list.
 *
 * Routes and Total rows are never moved or hidden.
 *
 * @param {Array<Array<unknown>>} entries
 * @param {string} month 'YYYY-MM'
 * @param {Record<string, string>} sections
 * @param {Map<string, {hiddenFrom: string | null, division: string | null}>} settings
 */
export function applyDriverSettings(entries, month, sections, settings) {
  const list = Array.isArray(entries) ? entries.map((e) => [...e]) : [];
  if (!(settings instanceof Map) || settings.size === 0) return list;
  const secs = sections && typeof sections === "object" ? sections : {};
  const sectionOf = (rows, idx) => {
    for (let i = idx + 1; i < rows.length; i += 1) if (rows[i][1] in secs) return rows[i][1];
    return null;
  };
  const targetOf = (division) => {
    const want = lower(division);
    return Object.keys(secs).find((k) => lower(secs[k]) === want) ?? null;
  };
  // Moves first, in list order, so each mover keeps its order among movers.
  const moving = [];
  let out = [];
  list.forEach((e, idx) => {
    const div = e[1] === "driver" ? settings.get(e[0])?.division : null;
    if (div && targetOf(div) !== sectionOf(list, idx)) moving.push({ e, to: targetOf(div) });
    else out.push(e);
  });
  for (const { e, to } of moving) {
    const at = to === null ? -1 : out.findIndex((x) => x[1] === to);
    if (at < 0) out.push(e);
    else out = [...out.slice(0, at), e, ...out.slice(at)];
  }
  return out.map((e) =>
    e[1] === "driver" && isDriverHidden(settings, e[0], month)
      ? [e[0], "hidden-driver", e[2], e[3]]
      : e,
  );
}

/**
 * The month-to-date total of one grid row: its cells summed over the days
 * the grid shows (every day through "Data through"), blanks as 0. The
 * division rows' Total column (Delta 2026-10-02: "a totals column out to
 * the side that keeps up with running monthly total").
 *
 * @param {Array<number | "">} vals
 */
export function rowTotal(vals) {
  let s = 0;
  for (const v of Array.isArray(vals) ? vals : [])
    if (typeof v === "number" && Number.isFinite(v)) s += v;
  return s;
}

/**
 * The division a list places a driver under: the division of the first
 * Total row after him, or, after the last Total row, the one roster
 * division no Total row closes (Delta's Dallas). Null when the driver is
 * not on the list or that tail division is not unique.
 *
 * @param {Array<Array<unknown>>} entries
 * @param {string} label
 * @param {Record<string, string>} sections
 * @param {string[]} divisions
 */
export function listedDivision(entries, label, sections, divisions) {
  const list = Array.isArray(entries) ? entries : [];
  const secs = sections && typeof sections === "object" ? sections : {};
  const idx = list.findIndex(
    (e) => e[0] === label && (e[1] === "driver" || e[1] === "hidden-driver"),
  );
  if (idx < 0) return null;
  for (let i = idx + 1; i < list.length; i += 1) if (list[i][1] in secs) return secs[list[i][1]];
  const closed = new Set(Object.values(secs).map(lower));
  const open = (Array.isArray(divisions) ? divisions : []).filter((d) => !closed.has(lower(d)));
  return open.length === 1 ? open[0] : null;
}
